/**
 * Website Form Leads API.
 *
 * Submissions pushed in from the public marketing website via
 * POST /api/public/website-leads (API-key protected). Values are sanitized
 * server-side before storage; these endpoints are the read/triage side used by
 * the Marketing page.
 */

import { API_BASE } from '../config/apiBase';
import { duplicateClientError } from './clients';
import type { CampaignAttribution } from './campaignAttribution';

export type WebsiteFormLeadStatus = 'Pending' | 'Approved' | 'Voided';

export const WEBSITE_FORM_LEAD_STATUSES: WebsiteFormLeadStatus[] = [
  'Pending',
  'Approved',
  'Voided',
];

/** Tags are either the lead's priority (at most one) or a market it belongs to. */
export type WebsiteFormLeadTagKind = 'priority' | 'field';

export interface WebsiteFormLeadTag {
  id?: number;
  kind: WebsiteFormLeadTagKind;
  label: string;
}

export const LEAD_PRIORITY_TAGS = ['High', 'Medium', 'Low'] as const;

/** Starting points for market tags; staff can type anything else. */
export const LEAD_FIELD_TAG_SUGGESTIONS = [
  'Hospitality',
  'Pharmaceutical',
  'Healthcare',
  'Senior Living',
  'Education',
  'Manufacturing',
  'Logistics',
  'Corporate Office',
  'Retail',
  'Government',
] as const;

export interface WebsiteFormLeadRow extends CampaignAttribution {
  id: number;
  first_name: string;
  last_name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
  sms_marketing_consent: boolean;
  area_of_interest: string;
  /** Which form or page on the website produced this, when the sender supplies it. */
  source: string;
  status: WebsiteFormLeadStatus;
  /** Internal follow-up notes written by staff. */
  notes: string;
  /** Original request body kept for auditing a misbehaving integration. */
  raw_payload: Record<string, string>;
  submitted_ip: string;
  status_changed_by_user_id: number | null;
  status_changed_at: string | null;
  /** Set once the submission has been added to the client database. */
  converted_client_id: number | null;
  tags: WebsiteFormLeadTag[];
  created_at: string | null;
  updated_at: string | null;
}

async function jsonOrThrow<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw duplicateClientError(res.status, data) ?? new Error((data as { error?: string }).error || fallback);
  }
  return data as T;
}

export async function fetchWebsiteFormLeads(userId: number): Promise<WebsiteFormLeadRow[]> {
  const res = await fetch(
    `${API_BASE}/api/marketing/website-leads?userId=${encodeURIComponent(userId)}`
  );
  const data = await jsonOrThrow<{ leads: WebsiteFormLeadRow[] }>(
    res,
    'Failed to fetch website form leads'
  );
  return data.leads ?? [];
}

export interface UpdateWebsiteFormLeadPayload {
  user_id: number;
  status?: WebsiteFormLeadStatus;
  notes?: string;
}

export async function updateWebsiteFormLead(
  id: number,
  payload: UpdateWebsiteFormLeadPayload
): Promise<WebsiteFormLeadRow> {
  const res = await fetch(`${API_BASE}/api/marketing/website-leads/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to update website form lead');
}

/** Replaces the lead's whole tag list. */
export async function updateWebsiteFormLeadTags(
  id: number,
  userId: number,
  tags: WebsiteFormLeadTag[]
): Promise<WebsiteFormLeadRow> {
  const res = await fetch(`${API_BASE}/api/marketing/website-leads/${id}/tags`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      tags: tags.map((t) => ({ kind: t.kind, label: t.label })),
    }),
  });
  return jsonOrThrow(res, 'Failed to update lead tags');
}

/** Values shown in the confirmation modal, editable before the lead is created. */
export interface ConvertWebsiteFormLeadPayload {
  user_id: number;
  company: string;
  point_of_contact: string;
  contact_email?: string;
  contact_phone?: string;
  source?: string;
  industry?: string;
  notes?: string;
}

/**
 * Adds the submission to the client database as a lead. The submission flips to
 * Approved and the sales team is emailed.
 */
export async function convertWebsiteFormLead(
  id: number,
  payload: ConvertWebsiteFormLeadPayload
): Promise<{ lead: WebsiteFormLeadRow; client_id: number }> {
  const res = await fetch(`${API_BASE}/api/marketing/website-leads/${id}/convert`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return jsonOrThrow(res, 'Failed to add lead to the client database');
}

/** Full name for display, falling back to the email local part or a placeholder. */
export function websiteFormLeadName(lead: WebsiteFormLeadRow): string {
  const name = `${lead.first_name} ${lead.last_name}`.trim();
  if (name) return name;
  if (lead.email) return lead.email.split('@')[0];
  return 'Unknown';
}
